(() => {
  'use strict';

  const existing = window.StrapBanditsHomepage;
  if (existing?.init) {
    existing.init(document);
    return;
  }

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class StrapBanditsSlider {
    constructor(root) {
      this.root = root;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.viewport = root.querySelector('[data-sb-viewport]');
      this.track = root.querySelector('[data-sb-track]');
      this.slides = Array.from(root.querySelectorAll('[data-sb-slide]'));
      this.previousButton = root.querySelector('[data-sb-prev]');
      this.nextButton = root.querySelector('[data-sb-next]');
      this.dots = root.querySelector('[data-sb-dots]');
      this.status = root.querySelector('[data-sb-status]');
      this.current = 0;
      this.positions = 1;
      this.visible = 1;
      this.scrollFrame = null;
      this.autoplayTimer = null;
      this.autoplayDelay = Number(root.dataset.sbAutoplay || 0);

      if (!this.viewport || !this.track || !this.slides.length) return;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.previous = this.previous.bind(this);
      this.next = this.next.bind(this);

      this.previousButton?.addEventListener('click', this.previous, { signal });
      this.nextButton?.addEventListener('click', this.next, { signal });
      this.viewport.addEventListener('scroll', this.onScroll, { passive: true, signal });
      this.viewport.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.previous();
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.next();
        }
      }, { signal });

      this.root.addEventListener('pointerenter', () => this.stopAutoplay(), { signal });
      this.root.addEventListener('pointerleave', () => this.startAutoplay(), { signal });
      this.root.addEventListener('focusin', () => this.stopAutoplay(), { signal });
      this.root.addEventListener('focusout', (event) => {
        if (!this.root.contains(event.relatedTarget)) this.startAutoplay();
      }, { signal });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.viewport);
      } else {
        window.addEventListener('resize', this.onResize, { passive: true, signal });
      }

      this.measure();
      this.startAutoplay();
    }

    measure() {
      if (!this.slides.length) return;

      const trackStyle = window.getComputedStyle(this.track);
      const gap = parseFloat(trackStyle.columnGap || trackStyle.gap || 0);
      const slideWidth = this.slides[0].getBoundingClientRect().width;
      const viewportWidth = this.viewport.clientWidth;

      if (!slideWidth || !viewportWidth) return;

      this.visible = Math.max(1, Math.floor((viewportWidth + gap) / (slideWidth + gap) + 0.02));
      this.positions = Math.max(1, this.slides.length - this.visible + 1);
      this.current = Math.min(this.current, this.positions - 1);
      this.buildDots();
      this.updateControls();
    }

    buildDots() {
      if (!this.dots) return;

      this.dots.replaceChildren();
      if (this.positions <= 1) {
        this.dots.hidden = true;
        return;
      }

      this.dots.hidden = false;
      for (let index = 0; index < this.positions; index += 1) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'sb-slider__dot';
        button.setAttribute('aria-label', `Go to slide ${index + 1} of ${this.positions}`);
        button.addEventListener('click', () => this.goTo(index), { signal: this.controller.signal });
        this.dots.append(button);
      }
    }

    getTargetLeft(index) {
      const slide = this.slides[Math.min(index, this.slides.length - 1)];
      if (!slide) return 0;
      return Math.max(0, slide.offsetLeft - this.track.offsetLeft);
    }

    goTo(index, behavior = prefersReducedMotion.matches ? 'auto' : 'smooth') {
      this.current = Math.max(0, Math.min(index, this.positions - 1));
      this.viewport.scrollTo({ left: this.getTargetLeft(this.current), behavior });
      this.updateControls();
    }

    previous() {
      this.goTo(this.current - 1);
    }

    next() {
      this.goTo(this.current + 1);
    }

    onScroll() {
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => {
        if (this.positions <= 1) return;
        let closestIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;

        for (let index = 0; index < this.positions; index += 1) {
          const distance = Math.abs(this.viewport.scrollLeft - this.getTargetLeft(index));
          if (distance < closestDistance) {
            closestIndex = index;
            closestDistance = distance;
          }
        }

        if (closestIndex !== this.current) {
          this.current = closestIndex;
          this.updateControls();
        }
      });
    }

    onResize() {
      const oldPositions = this.positions;
      this.measure();
      if (oldPositions !== this.positions) this.goTo(Math.min(this.current, this.positions - 1), 'auto');
    }

    updateControls() {
      if (this.previousButton) this.previousButton.disabled = this.current <= 0;
      if (this.nextButton) this.nextButton.disabled = this.current >= this.positions - 1;

      if (this.dots) {
        Array.from(this.dots.children).forEach((dot, index) => {
          dot.setAttribute('aria-current', index === this.current ? 'true' : 'false');
        });
      }

      if (this.status) {
        const lastVisible = Math.min(this.current + this.visible, this.slides.length);
        this.status.textContent = `Showing items ${this.current + 1} through ${lastVisible} of ${this.slides.length}`;
      }
    }

    startAutoplay() {
      this.stopAutoplay();
      if (!this.autoplayDelay || this.positions <= 1 || prefersReducedMotion.matches || document.hidden) return;
      this.autoplayTimer = window.setInterval(() => {
        this.goTo(this.current >= this.positions - 1 ? 0 : this.current + 1);
      }, this.autoplayDelay);
    }

    stopAutoplay() {
      if (this.autoplayTimer) window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    destroy() {
      this.stopAutoplay();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.resizeObserver?.disconnect();
      this.controller.abort();
      this.root.sbSlider = null;
    }
  }

  const setFormStatus = (form, message, isError = false) => {
    const status = form.querySelector('[data-sb-form-status]');
    if (!status) return;
    status.textContent = message;
    status.classList.toggle('is-error', isError);
  };

  const submitProductForm = async (form) => {
    if (form.classList.contains('is-loading')) return;

    const formData = new FormData(form);
    const variantId = formData.get('id');
    if (!variantId) {
      setFormStatus(form, 'Choose an option', true);
      return;
    }

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const submitButton = form.querySelector('[type="submit"]');
    const select = form.querySelector('[data-sb-variant-select]');
    let succeeded = false;

    form.classList.add('is-loading');
    submitButton?.setAttribute('aria-disabled', 'true');
    setFormStatus(form, 'Adding…');

    try {
      if (cart?.getSectionsToRender) {
        formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        cart.setActiveElement?.(document.activeElement);
      }

      const response = await fetch(window.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'This item could not be added.');
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        await publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'sb-homepage',
          productVariantId: variantId,
          cartData: result,
        });
      }

      if (cart?.renderContents && result.sections) {
        if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
        cart.renderContents(result);
      } else if (!cart && window.routes?.cart_url) {
        window.location.assign(window.routes.cart_url);
        return;
      }

      document.dispatchEvent(new CustomEvent('strapbandits:cart-added', { detail: result }));
      setFormStatus(form, 'Added to cart');
      succeeded = true;
    } catch (error) {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'sb-homepage',
          productVariantId: variantId,
          errors: error instanceof Error ? error.message : 'This item could not be added.',
          message: error instanceof Error ? error.message : 'This item could not be added.',
        });
      }
      setFormStatus(form, error instanceof Error ? error.message : 'This item could not be added.', true);
    } finally {
      form.classList.remove('is-loading');
      submitButton?.removeAttribute('aria-disabled');
      if (succeeded && select) select.selectedIndex = 0;
    }
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-sb-slider]').forEach((slider) => {
      if (slider.sbSlider) return;
      slider.sbSlider = new StrapBanditsSlider(slider);
    });
  };

  document.addEventListener('submit', (event) => {
    const form = event.target.closest?.('[data-sb-product-form]');
    if (!form) return;
    event.preventDefault();
    submitProductForm(form);
  });

  document.addEventListener('change', (event) => {
    const select = event.target.closest?.('[data-sb-variant-select]');
    if (!select?.value) return;
    const form = select.closest('[data-sb-product-form]');
    form?.requestSubmit();
  });

  document.addEventListener('visibilitychange', () => {
    document.querySelectorAll('[data-sb-slider]').forEach((slider) => {
      if (document.hidden) slider.sbSlider?.stopAutoplay();
      else slider.sbSlider?.startAutoplay();
    });
  });

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-sb-slider]').forEach((slider) => slider.sbSlider?.destroy());
  });
  prefersReducedMotion.addEventListener?.('change', () => {
    document.querySelectorAll('[data-sb-slider]').forEach((slider) => slider.sbSlider?.startAutoplay());
  });

  window.StrapBanditsHomepage = { init };
  init(document);
})();
